import type { ApolloError } from "@apollo/client";

import type { TUserGroup, TUserRank } from "MVVM/@types";
import type { TStatTone } from "MVVM/Views/Match/styledComponents";
import { BUFFER_TICKS } from "MVVM/Views/TimelineEditor/constants";
import type { IReplayCommand } from "MVVM/Views/TimelineEditor/types";
import type {
  TDuelKill,
  TMatchPageMoment,
  TMatchPagePlayer,
  TPlayerMatchStats,
} from "MVVM/Views/Match/@types";
import type {
  TStatAverages,
  TPlayerRating,
  TSide,
  TWinChanceRound,
} from "MVVM/Views/Match/useMatchRating";

// The API answers an unknown or malformed match id with a GraphQL error, not null.
const MATCH_NOT_FOUND =
  /Cannot return null for non-nullable field Query\.match\b|Invalid global identifier/;

export const isMatchNotFound = (error?: ApolloError) =>
  !!error &&
  !error.networkError &&
  error.graphQLErrors.some((entry) => MATCH_NOT_FOUND.test(entry.message));

// Round rides along in clipTags rather than its own field, same as the match
// history moment list reads it.
export const momentRound = (moment: TMatchPageMoment) => {
  const tag = moment.clipTags?.find((entry) => entry.key === "CS_Round");
  return tag ? Number(tag.value) : undefined;
};

export const momentTitle = (moment: TMatchPageMoment) =>
  moment.title || moment.timelineLabel || "Moment";

export const formatDuration = (seconds?: number | null) => {
  if (!seconds) return undefined;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${`${whole % 60}`.padStart(2, "0")}`;
};

export type TMomentState = "ready" | "rendering" | "failed" | "idle";

// One place decides what a moment is doing, so the tile, the counts and the
// poll can never disagree about it.
export const momentState = (
  moment: TMatchPageMoment,
  requested: string[],
): TMomentState => {
  if (moment.clip?.url) return "ready";
  const status = moment.clip?.status?.toUpperCase();
  if (status === "ERRORED" || status === "ERROR") return "failed";
  if (moment.clip || requested.includes(moment.id)) return "rendering";
  return "idle";
};

export type TMomentSort = "ready" | "best" | "round";

// Ready first: the page is about watching highlights, so what exists to watch
// leads. Everything else falls back to the score the backend already ranked by.
export const sortMoments = (
  moments: TMatchPageMoment[],
  requested: string[],
) => {
  const rank: Record<TMomentState, number> = {
    ready: 0,
    rendering: 1,
    idle: 2,
    failed: 3,
  };
  return [...moments].sort((a, b) => {
    const byState =
      rank[momentState(a, requested)] - rank[momentState(b, requested)];
    return byState !== 0 ? byState : (b.score ?? 0) - (a.score ?? 0);
  });
};

// The weapon the moment was made with. Lives in clipTags next to the round,
// same tag the clip badges read.
export const momentWeapon = (moment: TMatchPageMoment) =>
  moment.clipTags?.find((entry) => entry.key === "CS_Weapons")?.value;

const tagValues = (moment: TMatchPageMoment, key: string) =>
  (moment.clipTags || [])
    .filter((entry) => entry.key === key)
    .map((entry) => entry.value.toLowerCase());

export const momentKills = (moment: TMatchPageMoment) =>
  Number(tagValues(moment, "CS_Kill Count")[0]) ||
  moment.victimData?.length ||
  0;

// "1v2" style, from the same situational tag the clip badges read.
export const momentClutch = (moment: TMatchPageMoment) =>
  tagValues(moment, "CS_Situational").find((value) => /^1v\d$/.test(value));

// Only a per-moment count exists, so a victim is marked only when every kill was one.
export const allHeadshots = (moment: TMatchPageMoment) => {
  const kills = momentKills(moment);
  return kills > 0 && Number(tagValues(moment, "CS_Headshots")[0]) === kills;
};

export type THighlightFilter =
  | "all"
  | "ready"
  | "idle"
  | "multi"
  | "clutch"
  | "eco";

export const HIGHLIGHT_FILTERS: {
  key: THighlightFilter;
  label: string;
  noun: string;
  test: (moment: TMatchPageMoment, state: TMomentState) => boolean;
}[] = [
  { key: "all", label: "All", noun: "moments", test: () => true },
  {
    key: "ready",
    label: "Ready",
    noun: "ready moments",
    test: (_, state) => state === "ready" || state === "rendering",
  },
  {
    key: "idle",
    label: "Not clipped",
    noun: "unclipped moments",
    test: (_, state) => state === "idle" || state === "failed",
  },
  {
    key: "multi",
    label: "2K+",
    noun: "2K+ moments",
    test: (moment) => momentKills(moment) >= 2,
  },
  {
    key: "clutch",
    label: "Clutches",
    noun: "clutches",
    test: (moment) => !!momentClutch(moment),
  },
  {
    key: "eco",
    label: "Pistol & eco",
    noun: "pistol or eco moments",
    test: (moment) =>
      tagValues(moment, "CS_Round Type").some(
        (value) => value === "pistol" || value === "eco",
      ),
  },
];

// A column reads top to bottom, so the order is the ranking and the viewer gets
// to choose which ranking it is.
export const sortMomentsBy = (
  moments: TMatchPageMoment[],
  requested: string[],
  mode: TMomentSort,
) => {
  if (mode === "best") {
    return [...moments].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }
  if (mode === "round") {
    return [...moments].sort(
      (a, b) => (momentRound(a) ?? 0) - (momentRound(b) ?? 0),
    );
  }
  return sortMoments(moments, requested);
};

// Five bands, shared so every tab grades a number the same way.
export const toneFor = (
  value: number | null | undefined,
  elite: number,
  strong: number,
  even: number,
  weak: number,
): TStatTone => {
  if (value === null || value === undefined) return "even";
  if (value >= elite) return "elite";
  if (value >= strong) return "strong";
  if (value >= even) return "even";
  if (value >= weak) return "weak";
  return "poor";
};

export const kdRatio = (row: Pick<TPlayerMatchStats, "kills" | "deaths">) =>
  row.deaths === 0 ? row.kills : row.kills / row.deaths;

// One rule for who a player is, everywhere on this page. An Allstar account is
// the identity they chose, so it wins over the Steam persona when they have
// one; the Steam name stays available as the secondary label rather than being
// thrown away, because it is the name their teammates called them in game.
export const playerIdentity = (
  player: TMatchPagePlayer | undefined,
  fallback: string,
) => {
  const steamName = player?.displayName || undefined;
  const allstarName = player?.user?.username || undefined;
  return {
    // The name they played under in this match, not their Allstar username.
    name: steamName || allstarName || fallback,
    // Only worth showing when it differs from the name above it.
    alt: steamName && allstarName !== steamName ? allstarName : undefined,
    avatar: player?.user?.avatarUrl || player?.avatar || undefined,
    // A linked account counts even before its owner picks a username.
    isAllstar: !!player?.user,
    /* Undefined for unclaimed players, which is what short-circuits the
       hover profile card: AvatarV2 only mounts ProfileCardWrapper when it
       gets a username, so a Steam account with no Allstar profile has no
       hover target rather than an empty card. */
    username: allstarName,
    // Fed straight to AvatarV2, which decides the border and badge from these
    // two. FREE is its own default and draws nothing.
    userGroup: (player?.user?.userGroup?.type || undefined) as
      | TUserGroup
      | undefined,
    rank: (player?.user?.rank || undefined) as TUserRank | undefined,
  };
};

// You, then the people you played with, then the people you played against.
// The scoreboard's order, so the two surfaces agree.
export const orderedBySide = <T extends { gameId: string }>(
  players: T[],
  viewerGameId: string,
  teamOf: Record<string, string | undefined>,
) => {
  const viewerTeam = teamOf[viewerGameId];
  const weight = (player: T) => {
    if (player.gameId === viewerGameId) return 0;
    if (viewerTeam && teamOf[player.gameId] === viewerTeam) return 1;
    return 2;
  };
  return [...players].sort((a, b) => weight(a) - weight(b));
};

export const BAND_TONE: Record<string, TStatTone> = {
  Great: "elite",
  Good: "strong",
  Average: "even",
  Subpar: "weak",
  Poor: "poor",
};

export const ratingBand = (percentile: number) => {
  if (percentile >= 0.9) return "Great";
  if (percentile >= 0.7) return "Good";
  if (percentile >= 0.3) return "Average";
  if (percentile >= 0.1) return "Subpar";
  return "Poor";
};

export const signed = (value: number) =>
  `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(1)}`;

// An Allstar profile when they have one, otherwise their match history.
export const playerTarget = (username: string | undefined, id: string) =>
  username
    ? `/u/${username}`
    : `/match-history?playerSearchIdentifier=${encodeURIComponent(id)}`;

// MR12, then overtime in halves of 3 that keep the second-half side going in
// and swap after each. ponytail: other formats need the round's own side from quentin.
export const sideIn = (
  round: number,
  playerId: string,
  startedT: string[],
): TSide => {
  const startT = startedT.includes(playerId);
  const firstHalfSide =
    round <= 12 || (round > 24 && Math.floor((round - 25) / 3) % 2 === 1);
  return firstHalfSide === startT ? "T" : "CT";
};

export const pctText = (value: number) => `${Math.round(value * 100)}%`;

// Allstar Score of a plain single kill; only what a moment scores above it counts, as in Style.
const PLAIN_KILL = 30;
// Round credit at which a moment counts in full, the same value quentin's Style uses.
const FULL_CREDIT = 0.4477;

// Why a round's play mattered, from the round's Win Probability.
const playReason = (
  round: TWinChanceRound,
  viewerId: string,
  startedT: string[],
): string | undefined => {
  const side = sideIn(round.round, viewerId, startedT);
  if (
    round.lastAlive?.player === viewerId &&
    round.lastAlive.vs >= 2 &&
    round.winner === side
  ) {
    const low = side === "T" ? round.lowT : 1 - round.highT;
    return `Won the round when your team had a ${pctText(low)} chance`;
  }
  const top = round.events
    .filter((event) => event.actorId === viewerId)
    .map((event) => ({
      swing: side === "T" ? event.swingT : -event.swingT,
      chance: side === "T" ? event.chanceT : 1 - event.chanceT,
    }))
    .sort((a, b) => b.swing - a.swing)[0];
  return top && top.swing > 0
    ? `Raised your team's chance of winning the round to ${pctText(top.chance)}`
    : undefined;
};

/**
 * The spotlight moment, picked the way Style points are: what its Allstar Score beats a plain
 * kill by, counted in full when the viewer's swings that round reach FULL_CREDIT and at half
 * otherwise. Without Win Probability every moment counts in full, so the top score wins.
 */
export const bestPlay = <T extends { score?: number | null }>(
  moments: T[],
  roundOf: (moment: T) => number | undefined,
  viewerId: string,
  chart?: { rounds: TWinChanceRound[]; startedT: string[] } | null,
): { moment: T; reason?: string } | undefined => {
  const byRound = new Map(chart?.rounds.map((round) => [round.round, round]));
  // ponytail: credit here is the viewer's own positive swings; quentin's Style also counts damage and assists.
  const credit = (round?: TWinChanceRound) => {
    if (!round || !chart) return FULL_CREDIT;
    const side = sideIn(round.round, viewerId, chart.startedT);
    return round.events
      .filter((event) => event.actorId === viewerId)
      .reduce(
        (sum, event) =>
          sum + Math.max(0, side === "T" ? event.swingT : -event.swingT),
        0,
      );
  };
  const value = (moment: T) => {
    const weight =
      0.5 +
      0.5 *
        Math.min(1, credit(byRound.get(roundOf(moment) ?? -1)) / FULL_CREDIT);
    return Math.max(0, (moment.score ?? 0) - PLAIN_KILL) * weight;
  };
  const moment = [...moments].sort(
    (a, b) => value(b) - value(a) || (b.score ?? 0) - (a.score ?? 0),
  )[0];
  if (!moment) return undefined;
  const round = byRound.get(roundOf(moment) ?? -1);
  return {
    moment,
    reason:
      round && chart ? playReason(round, viewerId, chart.startedT) : undefined,
  };
};

export type TBestStat = {
  label: string;
  column: string;
  value: string;
  average: string;
  // Raw numbers, so the bars can be drawn to scale.
  raw: number;
  rawAverage: number;
  lower?: boolean;
  // How much better than usual, in the stat's own units: "+14%", "41 ms lower".
  better: string | null;
  // Set on a stat that dropped, shown as something to try working on.
  worse?: string;
  tone: TStatTone;
  // Place among the rated players in this match.
  place: number;
  of: number;
  badge?: "Best in the lobby" | "Best on your team";
};

type TStatDef = {
  label: string;
  // The matching scoreboard column, opened when the stat is clicked.
  column: string;
  // Counting stats everyone sees anyway rank below the more telling ones.
  basic?: boolean;
  // Lower is better for reaction and aim offset.
  lower?: boolean;
  // Null when the match has no value for it, like headshot accuracy before it was recorded.
  read: (row: TPlayerMatchStats, rating: TPlayerRating) => number | null;
  avg: keyof TStatAverages;
  format: (value: number) => string;
};

const BEST_STATS: TStatDef[] = [
  {
    label: "Kills",
    column: "kda",
    basic: true,
    read: (row) => row.kills,
    avg: "kills",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Damage per round",
    column: "adr",
    basic: true,
    read: (row) => row.adr ?? 0,
    avg: "adr",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "KAST",
    column: "kast",
    basic: true,
    read: (row) => row.kast ?? 0,
    avg: "kast",
    format: (v) => `${Math.round(v)}%`,
  },
  {
    label: "Utility damage",
    column: "utilDmg",
    read: (_, r) => r.stats.utilityDamage,
    avg: "utilityDamage",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Trade kills",
    column: "trades",
    read: (_, r) => r.stats.tradeKills,
    avg: "tradeKills",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Time to damage",
    column: "ttd",
    lower: true,
    read: (_, r) => r.stats.timeToDamageMs,
    avg: "timeToDamageMs",
    format: (v) => `${Math.round(v)} ms`,
  },
  {
    label: "Crosshair offset",
    column: "xhair",
    lower: true,
    read: (_, r) => r.stats.crosshairDeg,
    avg: "crosshairDeg",
    format: (v) => `${v.toFixed(1)}°`,
  },
  {
    label: "Opening duels won",
    column: "opening",
    read: (_, r) => r.stats.openingWins,
    avg: "openingWins",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Enemies flashed",
    column: "flashed",
    read: (_, r) => r.stats.enemiesFlashed,
    avg: "enemiesFlashed",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Flash assists",
    column: "flashAssists",
    read: (_, r) => r.stats.flashAssists,
    avg: "flashAssists",
    format: (v) => `${Math.round(v)}`,
  },
  {
    label: "Headshot accuracy",
    column: "hsAccuracy",
    read: (_, r) => r.stats.headshotAccuracy,
    avg: "headshotAccuracy",
    format: (v) => `${Math.round(v)}%`,
  },
  {
    label: "First-shot accuracy",
    column: "firstShot",
    read: (_, r) => r.stats.firstShotAccuracy,
    avg: "firstShotAccuracy",
    format: (v) => `${Math.round(v)}%`,
  },
  {
    label: "Counter-strafing",
    column: "strafe",
    read: (_, r) => r.stats.counterStrafing,
    avg: "counterStrafing",
    format: (v) => `${Math.round(v)}%`,
  },
  {
    label: "Headshot %",
    column: "hs",
    read: (row) => row.headshotPercentage ?? 0,
    avg: "headshotPercentage",
    format: (v) => `${Math.round(v)}%`,
  },
];

// The viewer's most telling stats: match or team leads first, then the biggest
// gains on the chosen comparison (their last 30, or players at their rank).
export const bestStats = (
  stats: TPlayerMatchStats[],
  ratings: Record<string, TPlayerRating>,
  viewer: TPlayerMatchStats,
  averages: TStatAverages | null,
  limit = 5,
): TBestStat[] => {
  const mine = ratings[viewer.playerGameId];
  if (!mine || !averages) return [];
  const rated = stats.filter((row) => ratings[row.playerGameId]);
  const scored = BEST_STATS.map((def) => {
    const value = def.read(viewer, mine);
    const average = averages[def.avg];
    if (value == null || average == null) return null;
    const gain = average
      ? ((def.lower ? average - value : value - average) / average) * 100
      : 0;
    const beats = (row: TPlayerMatchStats) => {
      const other = def.read(row, ratings[row.playerGameId]);
      if (other == null) return false;
      return def.lower ? other < value : other > value;
    };
    const place = rated.filter(beats).length + 1;
    const matchBest = place === 1;
    const teamBest =
      !matchBest &&
      !rated.filter((row) => row.team === viewer.team).some(beats);
    // Leading the match beats a big jump on your own average.
    const score =
      Math.min(gain, 100) * (def.basic ? 0.5 : 1) +
      (matchBest ? 60 : teamBest ? 25 : 0);
    return {
      gain,
      score,
      stat: {
        label: def.label,
        column: def.column,
        value: def.format(value),
        average: def.format(average),
        raw: value,
        rawAverage: average,
        lower: def.lower,
        better:
          (def.lower ? average - value : value - average) > 0
            ? def.lower
              ? `${def.format(average - value)} lower than usual`
              : `+${def.format(value - average)} vs usual`
            : null,
        place,
        of: rated.length,
        tone: (matchBest ? "elite" : gain > 0 ? "strong" : "even") as TStatTone,
        badge: matchBest
          ? ("Best in the lobby" as const)
          : teamBest
          ? ("Best on your team" as const)
          : undefined,
        worse:
          gain < 0
            ? def.lower
              ? `${def.format(value - average)} higher than usual`
              : `-${def.format(average - value)} vs usual`
            : undefined,
      },
    };
  }).filter((entry): entry is NonNullable<typeof entry> => !!entry);
  // Only stats that beat the comparison; a lobby lead alone is not enough.
  const up = scored
    .filter((entry) => entry.gain > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => ({ ...entry.stat, worse: undefined }));
  // The card is 5 tiles or 3; the biggest drops fill it, never more than 3.
  const room = Math.min((up.length >= 3 ? limit : 3) - up.length, 3);
  const down = scored
    .filter((entry) => entry.gain < 0)
    .sort((a, b) => a.gain - b.gain)
    .slice(0, Math.max(room, 0))
    .map((entry) => entry.stat);
  return [...up, ...down];
};

// Same fallback the profile card banner uses.
const DEFAULT_COVER = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/banner/allstar.jpg`;

// Video covers need a player, so those fall back to Allstar's banner too.
export const coverImageOf = (player?: TMatchPagePlayer) => {
  const cover = player?.user?.coverImageUrl;
  return cover && !/\.(mp4|webm)$/i.test(cover) ? cover : DEFAULT_COVER;
};

// First signal that has a plain reading wins; the parser lists the strongest first.
const STARGUARD_REASONS: [string[], string][] = [
  [["silentAim", "snap", "aimOffset", "spinKill"], "Snapped onto you"],
  [
    ["victimNeverSpottedByTeam", "victimNeverSpotted"],
    "Shot you before your team had seen them",
  ],
  [["throughSmoke", "throughSmoke+wallBang"], "Killed you through smoke"],
  [["wallBang"], "Killed you through a wall"],
  [["occludedTracking"], "Tracked you through a wall"],
  [["triggerTiming"], "Reacted faster than humanly likely"],
];

export const STARGUARD_FLAG = 0.5;

export const starGuardReason = (signals: string[]) => {
  for (const signal of signals) {
    const hit = STARGUARD_REASONS.find(([names]) => names.includes(signal));
    if (hit) return hit[1];
  }
  return "StarGuard flagged this kill";
};

export const isWorthChecking = (kill: TDuelKill, viewerId: string) =>
  kill.victim === viewerId && (kill.starGuard?.score ?? 0) >= STARGUARD_FLAG;

const DUEL_LEAD_SECONDS = 6;
const DUEL_AFTER_SECONDS = 2;
const DUEL_MAX_KILLS = 15;
const DUEL_MAX_SECONDS = 120;

// One clip of several kills, every cut spectating the same player, cut the
// way the timeline editor cuts jump cuts.
export const duelReplaySequence = (
  killTicks: number[],
  pov: { steamId: string; slot: number },
  tickRate: number,
) => {
  const sequence: IReplayCommand[] = [
    { tick: 0, class: "execCommand", command: "cl_draw_only_deathnotices 1" },
    { tick: 0, class: "execCommand", command: "spec_show_xray 1" },
  ];
  let lastTick = 0;
  let end = -Infinity;
  let ticks = 0;
  let segments = 0;

  [...killTicks]
    .sort((a, b) => a - b)
    .forEach((tick) => {
      const start = tick - DUEL_LEAD_SECONDS * tickRate;
      const stop = tick + DUEL_AFTER_SECONDS * tickRate;
      // A kill inside the previous cut just extends it.
      if (segments && start <= end + 2 * tickRate) {
        if (ticks + stop - end > DUEL_MAX_SECONDS * tickRate) return;
        ticks += stop - end;
        sequence[sequence.length - 1].tick = stop;
        end = stop;
        lastTick = stop + 64;
        return;
      }
      const from = Math.max(start, BUFFER_TICKS);
      if (from >= stop || segments >= DUEL_MAX_KILLS) return;
      if (ticks + stop - from > DUEL_MAX_SECONDS * tickRate) return;

      sequence.push(
        {
          tick: lastTick,
          class: "gotoTickAndSpectatePlayer",
          playerSlot: pov.slot,
          gotoTick: from - BUFFER_TICKS,
          steamId64: pov.steamId,
        },
        { tick: from, class: "startRecord" },
        { tick: stop, class: "endRecord" },
      );
      ticks += stop - from;
      segments += 1;
      end = stop;
      lastTick = stop + 64;
    });

  return { sequence, segments, seconds: ticks / tickRate };
};

const agentUrl = (id: number) =>
  `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/cs2_agents/${id}.png`;
// ponytail: stand-ins for the default models (5036 T, 5037 CT), which have no image yet.
export const DEFAULT_AGENT = { CT: agentUrl(4619), T: agentUrl(4613) } as const;
const DEFAULT_AGENT_IDS = new Set([5036, 5037]);

// The agent from the side they finished on, else their other side.
export const agentImage = (row: TPlayerMatchStats, side: "T" | "CT") => {
  const id =
    side === "CT" ? row.ctAgent ?? row.tAgent : row.tAgent ?? row.ctAgent;
  return id && !DEFAULT_AGENT_IDS.has(id) ? agentUrl(id) : DEFAULT_AGENT[side];
};

// The Allstar star, drawn by the rendering and rating loaders.
export const STAR =
  "M12 1.8l3.1 6.6 7.2.8-5.4 4.9 1.5 7.1L12 17.6l-6.4 3.6 1.5-7.1-5.4-4.9 7.2-.8z";
